import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { I18N_KEYS } from '@/language/keys';
import { WorkspaceRole } from '@/types/auth.types';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import type { MenuAction } from '@/components/Menu/Actions';

interface RoleSelectProps {
  currentRole: WorkspaceRole;
  disabled?: boolean;
  updateMemberRole: (role: WorkspaceRole) => void;
}

export const RoleSelect = ({ currentRole, disabled = false, updateMemberRole }: RoleSelectProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const roleLabels = useMemo<Record<WorkspaceRole.Admin | WorkspaceRole.Member, string>>(
    () => ({
      [WorkspaceRole.Admin]: t(I18N_KEYS.SETTINGS.TEAM.ROLES.ADMIN),
      [WorkspaceRole.Member]: t(I18N_KEYS.SETTINGS.TEAM.ROLES.MEMBER),
    }),
    [t],
  );

  const items = useMemo<MenuAction[]>(
    () =>
      ([WorkspaceRole.Admin, WorkspaceRole.Member] as const).map((role) => ({
        biEventKey: { menu: MENU_NAMES.TEAM_ROLE_SELECT, action: 'change_role' },
        name: roleLabels[role],
        selected: currentRole === role,
        action: () => updateMemberRole(role),
      })),
    [roleLabels, currentRole, updateMemberRole],
  );

  return (
    <>
      <ButtonContained
        mode="text"
        size="small"
        disabled={disabled}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        endIcon={
          <CaretIcon style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
        }
      >
        {roleLabels[currentRole as WorkspaceRole.Admin | WorkspaceRole.Member] ?? currentRole}
      </ButtonContained>
      <AppContextMenu open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} items={items} width="120px" />
    </>
  );
};
